import React, { useEffect, useState } from 'react';
import loadable from '@loadable/component';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { searchContent } from '@plone/volto/actions';
import { Icon } from '@plone/volto/components';
import cx from 'classnames';

const MapComponent = loadable(() => import('./MapComponent'), { 
  ssr: false,
  fallback: <div>Loading map...</div>
});

const MappaTemplate = (props) => {
  const {
    cardBackground = 'no-background',
    linkText,
    linkUrl,
    alignButtonRight = false,
    altezzaMappa = 'medium-map',
    querystring = {},
    data = {},
  } = props;

  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const blockId = data?.block || 'mappa';
  const dispatch = useDispatch();

  // Stato della ricerca
  const searchResults = useSelector((state) => state.search.subrequests?.[blockId] || {});
  const { items = [], loading, error } = searchResults;

//console.log(items);

  // Effettua la ricerca
  useEffect(() => {
    if (querystring?.query && Array.isArray(querystring.query)) {
      const searchParams = {
        fullobjects: 1,
        metadata_fields: ['latitudine', 'longitudine', 'indirizzo', 'cap', 'testodescrittivo'],
      };
  
      querystring.query.forEach((crit) => {
        if (crit.i && crit.v !== undefined) {
          switch (crit.i) {
            case 'sort_on':
            case 'sort_order':
            case 'b_size':
              searchParams[crit.i] = Array.isArray(crit.v) ? crit.v[0] : crit.v;
              break;
            case 'path':
              // ✅ Usa path come stringa, non come oggetto
              searchParams.path = Array.isArray(crit.v) ? crit.v[0] : crit.v;
              break;
            default:
              searchParams[crit.i] = crit.v;
              break;
          }
        }
      });
  
      // ✅ Applica limit se presente
      if (querystring.limit) {
        const limit = parseInt(querystring.limit);
        if (!isNaN(limit)) {
          searchParams.b_size = limit;
        }
      }
  
      //console.log('Parametri di ricerca corretti:', searchParams);
  
      dispatch(
        searchContent(
          '', // lascia vuoto per usare path nel searchParams
          searchParams,
          blockId,
        )
      );
    }
  }, [dispatch, querystring, blockId]);
  
  

  // Mappa i dati per la mappa
  useEffect(() => {
    const newLocations = items
      .filter((item) => item['@type'] === 'Luogo')
      .map((item) => {
        const lat = parseFloat(item.latitudine);
        const lng = parseFloat(item.longitudine);
        const hasValidCoords = !isNaN(lat) && !isNaN(lng);

        return {
          id: item['@id'],
          title: item.title,
          coordinates: hasValidCoords ? [lat, lng] : null,
          address: [item.indirizzo, item.cap].filter(Boolean).join(', '),
          description: item.testodescrittivo,
          isValid: hasValidCoords,
        };
      })
      .filter(loc => loc.isValid);

      
    //console.log('Luoghi trovati:', newLocations.length);
    setLocations(newLocations);
  }, [items]);

const handleMarkerClick = (location) => {
  setSelectedLocation(location);
};

  // Mappa le scelte dell'altezza a valori in pixel
  const getMapHeight = () => {
    switch(altezzaMappa) {
      case 'short-map':
        return '400px';
      case 'medium-map':
        return '550px';
      case 'big-map':
        return '700px';
      default:
        return '550px'; // Default
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <Icon name="spinner" size="30px"  color='' className='' title='' />
        <p>Caricamento luoghi in corso...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-message">
        <Icon name="warning" size="30px" color='' className='' title='' />
        <p>Errore nel caricamento dei luoghi: {error.message}</p>
      </div>
    );
  }

  const blockClasses = cx('block-lista-luoghi', {
    'no-background': cardBackground === 'no-background',
    'blue-background': cardBackground === 'blue-background',
    'pastel-blue-background': cardBackground === 'pastel-blue-background',
  });

  const buttonClasses = cx('mappa-button', {
    'right': alignButtonRight,
    'center': !alignButtonRight,
  });

  const mapStyle = {
    height: getMapHeight(),
    width: '100%',
    borderRadius: '8px',
    /*border: '1px solid #ddd'*/
  };

  return (
    <div className={`full-width-background-wrapper ${blockClasses}`}>
      <div className="map-wrapper mappa_view" style={mapStyle}>
        {locations.length > 0 ? (
          <MapComponent locations={locations} altezzaMappa={altezzaMappa} onMarkerClick={handleMarkerClick} />
        ) : (
          <p className="no-results">Nessun luogo trovato con coordinate valide.</p>
        )}
      </div>
      
      {linkUrl && (
        <div className={`link-button my-4 ${alignButtonRight ? 'align-right' : ''}`}>
          <a
            href={linkUrl}
            className={`btn btn-primary`}
          >
            {linkText || 'Scopri di più'}
          </a>
        </div>
      )}
    </div>
  );
};

MappaTemplate.propTypes = {
  cardBackground: PropTypes.string,
  linkText: PropTypes.string,
  linkUrl: PropTypes.string,
  alignButtonRight: PropTypes.bool,
  altezzaMappa: PropTypes.oneOf(['short-map', 'medium-map', 'big-map']),
  data: PropTypes.object,
};

MappaTemplate.defaultProps = {
  cardBackground: 'no-background',
  alignButtonRight: false,
  altezzaMappa: 'medium-map',
};

export default MappaTemplate;